import React from "react";
import OnboardingHeader from "../OnboardingComponents/OnboardingHeader";
import OnboardingCard from "../OnboardingComponents/OnboardingCard";
import OnboardingButton from "../OnboardingComponents/OnboardingButton";
import OnboardingSelect from "../OnboardingComponents/OnboardingSelect";

export interface SelectOption {
  label: string;
  value: string;
}

export interface AuthResponse {
  user?: {
    firstName?: string;
  } | null;
}

interface CallbackSuccessStateProps {
  authResponse: AuthResponse;
  userEmail?: string;
  roleOptions: SelectOption[];
  selectedRole: SelectOption | null;
  setSelectedRole: (role: SelectOption) => void;
  userOptions: SelectOption[];
  selectedAssignedUser: SelectOption | null;
  setSelectedAssignedUser: (user: SelectOption | null) => void;
  isProcessingAssigned: boolean;
  onLoginAsSelf: () => void;
  onLoginAsAssigned: () => void;
}

const CallbackSuccessState: React.FC<CallbackSuccessStateProps> = ({
  authResponse,
  userEmail,
  roleOptions,
  selectedRole,
  setSelectedRole,
  userOptions,
  selectedAssignedUser,
  setSelectedAssignedUser,
  isProcessingAssigned,
  onLoginAsSelf,
  onLoginAsAssigned,
}) => {
  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <OnboardingHeader />

      <main className="w-full max-w-5xl mx-auto p-10 text-left">
        <OnboardingCard className="p-8 sm:p-10 mt-8">
          
          <div className="mb-12 mt-4 text-left">
            <h2 className="text-left text-3xl font-bold text-[#030724] font-['Canela_Text']">
              {authResponse.user?.firstName
                ? `Welcome, ${authResponse.user.firstName}`
                : "Welcome Back"}
            </h2>
            {userEmail && (
              <p className="text-xl font-normal text-[#46464D] mt-2 font-['Proxima_Nova'] break-all">
                Authenticated as {userEmail}
              </p>
            )}
          </div>

          <div className="flex flex-col gap-12">
            
            {authResponse.user && (
              <div>
                <OnboardingButton
                  title={`CONTINUE AS ${authResponse.user.firstName?.toUpperCase() || "YOURSELF"}`}
                  onClick={onLoginAsSelf}
                  variant="filled"
                />
              </div>
            )}

            {roleOptions.length > 0 && authResponse.user && (
              <div className="flex items-center gap-4">
                <div className="flex-1 h-[2px] bg-[#A2B2C833]"></div>
                <span className="font-['Proxima_Nova'] font-bold text-lg text-[#A2B2C8] uppercase tracking-widest">
                  Or act on behalf of
                </span>
                <div className="flex-1 h-[2px] bg-[#A2B2C833]"></div>
              </div>
            )}

            {roleOptions.length > 0 && (
              <div className="flex flex-col gap-8">
                
                <div className="flex flex-col gap-4">
                  <label className="text-xl font-normal text-[#46464D] font-['Proxima_Nova']">
                    Select your role
                  </label>
                  <div className="flex flex-col sm:flex-row gap-4 mb-4">
                    {roleOptions.map((role) => (
                      <div key={role.value} className="flex-1">
                        <OnboardingButton
                          title={role.label}
                          onClick={() => setSelectedRole(role)}
                          variant={selectedRole?.value === role.value ? "filled" : "outline"}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mb-8">
                  <OnboardingSelect
                    label="Select client"
                    options={userOptions}
                    placeholder={
                      selectedRole
                        ? "Choose a client"
                        : "Select a role first"
                    }
                    value={selectedAssignedUser?.value || ""}
                    onChange={(val) => {
                      const user = userOptions.find((u) => u.value === val);
                      setSelectedAssignedUser(user || null);
                    }}
                  />
                </div>

                <div>
                  <OnboardingButton
                    title={isProcessingAssigned ? "SIGNING IN..." : "SIGN IN AS ASSIGNED USER"}
                    onClick={onLoginAsAssigned}
                    disabled={
                      !selectedRole ||
                      !selectedAssignedUser ||
                      isProcessingAssigned
                    }
                    variant="filled"
                  />
                </div>
              </div>
            )}
          </div>
        </OnboardingCard>
      </main>
    </div>
  );
};

export default CallbackSuccessState;